import { LifeBuoy } from 'lucide-react';
import { apiKey } from "../../networking/urlEndPoints";
import { fetchAllRecentChats } from "../../services/actions/chat/recentChatsListsActions";
import { getInitiateChats } from "../../services/reducers/pharmacy/sms/pharmacySms";
import { toast } from 'react-toastify';
import { playNotificationSound } from '../../utils/audio/notification';
import { showBrowserNotification } from '../../utils/miscellaneous/browserNotification';
import { ablyConnectionManager } from '../../utils/miscellaneous/ablyConnectionManager';
import { subscribeRealtimeEvent } from '../../services/realtime/realtimeSocket';
import { isPortalChatOpen } from '../../services/chat/chatRefreshGate';

export const SUPPORT_CHAT_OPEN_EVENT = 'oatrx:support-chat:open';
const SUPPORT_CHAT_BRAND_BLUE = '#2626E0';
const SUPPORT_NOTIFICATION_TOAST_STYLE = {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    padding: '16px',
    minHeight: 'auto',
    margin: 0,
};

export const openSupportChat = (threadId = null) => {
    window.dispatchEvent(new CustomEvent(SUPPORT_CHAT_OPEN_EVENT, {
        detail: { threadId: threadId || null },
    }));
};

const formatSupportStatusLabel = (status = '') => {
    const normalized = String(status || '').trim().toLowerCase();
    if (!normalized) return 'Updated';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const getSupportTicketTitle = (eventData = {}) => (
    String(
        eventData?.thread_title
        || eventData?.title
        || eventData?.conversation_title
        || ''
    ).trim()
);

const SupportTicketNotificationContent = ({
    headline,
    badgeLabel,
    badgeTone = 'reply',
    primaryLine,
    preview,
}) => (
    <div className="flex cursor-pointer items-start gap-3">
        <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white"
            style={{ backgroundColor: SUPPORT_CHAT_BRAND_BLUE }}
        >
            <LifeBuoy size={20} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
                <p className="rubik-500 text-sm font-semibold text-gray-900">{headline}</p>
                <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        badgeTone === 'status'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-indigo-100 text-indigo-700'
                    }`}
                >
                    {badgeLabel}
                </span>
            </div>
            {primaryLine ? (
                <p className="mt-1 truncate text-sm text-gray-900">{primaryLine}</p>
            ) : null}
            {preview ? (
                <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{preview}</p>
            ) : null}
        </div>
    </div>
);

// Helper to create a cleanup wrapper that tracks subscriptions
const createSubscriptionWrapper = (subscriptionIds) => {
	return {
		close: () => {
			if (Array.isArray(subscriptionIds)) {
				subscriptionIds.forEach(id => ablyConnectionManager.unsubscribe(id));
			} else if (subscriptionIds) {
				ablyConnectionManager.unsubscribe(subscriptionIds);
			}
		}
	};
};

const createCombinedSubscriptionWrapper = (...subscriptions) => ({
	close: () => {
		subscriptions.filter(Boolean).forEach((subscription) => {
			if (typeof subscription.close === 'function') {
				subscription.close();
			}
		});
	},
});

export const subscribeGroupMessages = (group_id, setChatAppendedMessages, setMyInputMessages, selectedEmployeeId) => {
    if (!apiKey) {
        console.error('Ably API key is not configured');
        return null;
    }

    if (!group_id) {
        console.warn('No group ID provided for Ably subscription');
        return null;
    }

    const channelName = 'driver-pharmacist-chat-channel';
    const channel = ablyConnectionManager.getChannel(channelName);
    if (!channel) return null;

    const sendChatMessages = `message-group-${group_id}`;
    const subscriptionId = `${channelName}-${sendChatMessages}`;

    const callback = async (message) => {
        if (message.name === sendChatMessages) {
            setChatAppendedMessages((prev) => {
                const messageExists = prev.some((msg) => msg.message_id === message.data.message_id);

                if (!messageExists) {
                    return [...prev, message?.data];
                }

                return prev;
            });
            setMyInputMessages([])
        }
    };

    ablyConnectionManager.subscribe(channelName, sendChatMessages, callback, subscriptionId);
    const realtimeSubscription = subscribeRealtimeEvent(sendChatMessages, callback);
    return createCombinedSubscriptionWrapper(createSubscriptionWrapper(subscriptionId), realtimeSubscription);
}

export const subScribeChatNotifications = (pharmacyId,
    userId,
    dispatch) => {
    if (!apiKey) {
        console.error('Ably API key is not configured');
        return null;
    }

    if (!pharmacyId || !userId) {
        console.warn('Missing pharmacyId or userId for chat notifications');
        return null;
    }

    const channelName = 'pharmacy-common-chat-notification';
    const channel = ablyConnectionManager.getChannel(channelName);
    if (!channel) return null;

    const messageNotification = `message-notification-${pharmacyId}-${userId}`;
    const subscriptionId = `${channelName}-${messageNotification}`;

    const callback = (message) => {
        if (messageNotification === message.name) {
            // Keep browser notifications working everywhere, but refresh the
            // recent-chat API only while its panel is open.
            if (isPortalChatOpen()) {
                dispatch(fetchAllRecentChats())
            }
            enableNotifications(
                message?.data?.title,
                message?.data?.body,
                message?.data?.thumbnail,
                message?.data?.pharmacy_logo
            )
        }
    };

    ablyConnectionManager.subscribe(channelName, messageNotification, callback, subscriptionId);
    const realtimeSubscription = subscribeRealtimeEvent(messageNotification, callback);
    return createCombinedSubscriptionWrapper(createSubscriptionWrapper(subscriptionId), realtimeSubscription);
}

const enableNotifications = (title, body, thumbnail, logo) => {
    showBrowserNotification(title, {
        body,
        icon: thumbnail || logo,
    });
}

const isCurrentBrowserTabActive = () => {
    if (typeof document === 'undefined') return true;

    const isVisible = document.visibilityState === 'visible';
    const hasFocus = typeof document.hasFocus !== 'function' || document.hasFocus();

    return isVisible && hasFocus;
};

const showSupportChatNotification = (eventData) => {
    const eventType = String(eventData?.event_type || '').trim();
    const threadId = eventData?.thread_id || '';
    const toastId = `support-chat-${eventData?.message_id || `${eventType}-${threadId}-${eventData?.status || ''}-${eventData?.changed_at || eventData?.sent_at || ''}`}`;

    if (eventType === 'message.agent_reply') {
        const preview = String(eventData?.body || '').trim();
        const agentName = String(eventData?.agent_name || 'Support').trim();
        const ticketTitle = getSupportTicketTitle(eventData);
        const message = preview || 'New support reply received';

        playNotificationSound();
        toast(
            <SupportTicketNotificationContent
                headline="New support reply"
                badgeLabel="Reply"
                badgeTone="reply"
                primaryLine={ticketTitle || agentName}
                preview={ticketTitle ? `${agentName}: ${message}` : message}
            />,
            {
                toastId,
                position: 'top-right',
                autoClose: 6000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                icon: false,
                className: 'support-ticket-message-toast !w-[330px] !max-w-[330px]',
                bodyClassName: '!m-0 !p-0',
                style: SUPPORT_NOTIFICATION_TOAST_STYLE,
                onClick: () => openSupportChat(threadId),
            }
        );

        if (window.Notification?.permission === 'granted') {
            new window.Notification('New support reply', { body: `${agentName}: ${message}` });
        }
        return;
    }

    if (eventType === 'conversation.status_changed') {
        const status = String(eventData?.status || '').trim();
        const statusLabel = formatSupportStatusLabel(status);
        const changedBy = String(eventData?.changed_by || 'Support').trim();
        const ticketTitle = getSupportTicketTitle(eventData);
        const preview = ticketTitle
            ? `Status changed to ${statusLabel}`
            : (status ? `Support ticket ${statusLabel}` : 'Support ticket status updated');

        toast(
            <SupportTicketNotificationContent
                headline="Ticket updated"
                badgeLabel={statusLabel}
                badgeTone="status"
                primaryLine={ticketTitle || changedBy}
                preview={ticketTitle ? preview : `${changedBy} updated this ticket`}
            />,
            {
                toastId,
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                icon: false,
                className: 'support-ticket-message-toast !w-[330px] !max-w-[330px]',
                bodyClassName: '!m-0 !p-0',
                style: SUPPORT_NOTIFICATION_TOAST_STYLE,
                onClick: () => openSupportChat(threadId),
            }
        );
        return;
    }
};


export const subScribeChatMessages = (pharmacyId, userId, dispatch) => {
    if (!apiKey) {
        console.error('Ably API key is not configured');
        return null;
    }

    if (!pharmacyId) {
        console.warn('Missing pharmacyId for chat messages');
        return null;
    }

    const channelName = `oat-pharmacy-twillo-sms-${pharmacyId}`;
    const channel = ablyConnectionManager.getChannel(channelName);
    if (!channel) return null;

    const messageNotification = `new_message`;
    const subscriptionId = `${channelName}-${messageNotification}`;

    // Track processed message IDs to prevent duplicate notifications
    const processedMessageIds = new Set();

    const callback = (message) => {
        // Check if this message has already been processed
        const messageId = message?.data?.message_id;
        if (messageId && processedMessageIds.has(messageId)) {
            return; // Skip if already processed
        }
        
        // Add message ID to processed set
        if (messageId) {
            processedMessageIds.add(messageId);
        }
        
        if (userId) {
            dispatch(getInitiateChats({
                requestType: 'pharmacyGetConversationMessages',
                requestData: {
                    pharmacyId,
                    threadId: userId
                }
            }));
        }

        if (pharmacyId) {
            // Play notification sound
            playNotificationSound();
            
            // Show enhanced toast notification with dynamic patient name
            const patientName = message?.data?.patient_name?.slice(0,1)?.toUpperCase() + message?.data?.patient_name?.slice(1) || '';
            const patient_phone = message?.data?.patient_phone;

            toast(patientName ? `New message from \n${patientName} - ${patient_phone}` : 'Received a new message', {
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                className: 'custom-toast',
                bodyClassName: 'custom-toast-body gap-4',
                icon: ({ theme, type }) => (
                    <div className="w-8 h-8 bg-[#34D399] rounded-full flex items-center justify-center">
                        <svg width="70" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="white"/>
                        </svg>
                    </div>
                ),
                style: {
                    background: '#F3F4F6',
                    color: '#111827',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    whiteSpace: 'pre-line'
                },
                onClick: (event) => {
                    event.preventDefault();
                    window.focus();
                    dispatch(getInitiateChats({
                        requestType: 'pharmacyGetConversationMessages',
                        requestData: {
                            pharmacyId,
                            threadId: message?.data?.thread_id
                        }
                    }));
                    window.open(`/pharmacy-sms?active=text-messages&thread_id=${message?.data?.thread_id}`, '_blank');
                }
            });
        }
    };

    ablyConnectionManager.subscribe(channelName, messageNotification, callback, subscriptionId);
    return createSubscriptionWrapper(subscriptionId);
}

export const subscribeSupportChatEvents = (pharmacyId, onSupportEvent) => {
    if (!pharmacyId) return null;

    const eventName = 'support-chat-updated';
    const processedEventIds = new Set();

    const callback = (message) => {
        const eventData = message?.data?.data || message?.data || {};

        if (Number(eventData.pharmacy_id) !== Number(pharmacyId)) {
            return;
        }

        const eventId = eventData.message_id
            || `${eventData.event_type || 'support'}-${eventData.thread_id || ''}-${eventData.status || ''}-${eventData.changed_at || eventData.sent_at || ''}`;

        if (eventId && processedEventIds.has(eventId)) {
            return;
        }
        if (eventId) processedEventIds.add(eventId);

        onSupportEvent?.(eventData);
        if (isCurrentBrowserTabActive()) {
            showSupportChatNotification(eventData);
        }
    };

    return subscribeRealtimeEvent(eventName, callback);
}

export const MedicationReviewToast1 = ({ onClose }) => {
    return (
      <div className="fixed bottom-6 left-6 z-50">
        <div className="rounded-xl border w-[420px] p-0 bg-white shadow-lg transform transition-all duration-200 ease-in-out hover:shadow-xl">
          <div className="relative">
            {/* Decorative Top Bar */}
            <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-t-xl"></div>
            
            <div className="p-5">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-gray-500">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>

              {/* Content */}
              <div className="space-y-4">
                {/* Header with Icon */}
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-50">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-blue-600">
                      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                      <path d="m9 12 2 2 4-4"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">Follow-Up Reminder (Due Today)</h3>
                  </div>
                </div>

                {/* Message Content */}
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="space-y-1">
                    <p className="text-gray-900 font-bold">Med Review follow-up due for <span className="uppercase">Patrick, Allen</span></p>
                    <p className="text-sm text-gray-600">Please complete the review today.</p>
                  </div>
                </div>

                {/* Action Button */}
                <button className="w-full inline-flex items-center justify-center text-sm bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 h-11 rounded-lg px-4 font-medium transition-all duration-200 transform hover:scale-[1.02]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2">
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                  Review Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
}
  





